import { UserService } from 'src/app/user/services/user.service';

import { PhoneNumberPipe } from '../../../../../shared/phoneNumber.pipe';
import { IsNotEmptyObjectPipe } from '../../../../../shared/pipes/is-not-empty-object.pipe';
import { HasThresholdsPipe } from '../../pipes/has-thresholds.pipe';
import { IsNilPipe } from 'src/app/pipes/is-nil.pipe';
import { CountUserProductStationsPipe } from 'src/app/user/pipes/count-user-product-stations.pipe';
import { CountUserProductsPipe } from 'src/app/user/pipes/count-user-products.pipe';
import { HasUserEopProductPipe } from 'src/app/user/pipes/has-user-eop-product.pipe';
import { HasUserEppProductPipe } from 'src/app/user/pipes/has-user-epp-product.pipe';
import { HasUserProductPipe } from 'src/app/user/pipes/has-user-product.pipe';
import { HasUserProductTypePipe } from 'src/app/user/pipes/has-user-product-type.pipe';
import { HasUserStationPipe } from 'src/app/user/pipes/has-user-station.pipe';
import { VerifyPhoneNumberPipe } from 'src/app/user/pipes/verify-phone-number.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { AddEvent } from '../../models/add-event.interface';
import { ChangePause } from '../../models/change-pause.interface';
import { DeliveryChange } from '../../models/delivery-change.interface';
import { RemoveEvent } from '../../models/remove-event.interface';
import { MatErrorMessagesDirective } from '@wettermanufaktur-gmbh/ui-sdk/directives/mat-error-messages';
import { map, merge, startWith } from 'rxjs';

export const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  MatCardModule,
  MatCheckboxModule,
  MatFormFieldModule,
  MatInputModule,
  MatDatepickerModule,
  MatTooltipModule,
  MatChipsModule,
  MatIconModule,
  RouterModule,
  MatListModule,
  MatExpansionModule,
  MatSelectModule,
  MatButtonModule,
  MatStepperModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  HasThresholdsPipe,
  PhoneNumberPipe,
  IsNotEmptyObjectPipe,
  IsNilPipe,
  HasUserEppProductPipe,
  HasUserEopProductPipe,
  HasUserProductPipe,
  CountUserProductStationsPipe,
  CountUserProductsPipe,
  HasUserStationPipe,
  HasUserProductTypePipe,
  VerifyPhoneNumberPipe,
];
export const DIRECTIVES = [MatErrorMessagesDirective];

@Component({
  selector: 'app-users-tab-shipping',
  templateUrl: './users-tab-shipping.component.html',
  styleUrls: [
    '../../../../user-admin.component.scss',
    './users-tab-shipping.component.scss',
  ],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class UsersTabShippingComponent implements OnInit {
  userService = inject(UserService);
  private injector = inject(Injector);

  newUser = input<boolean>();
  egwsummerThresholdOptions = input<any>();
  isSaving = input<boolean>();
  isMaster = input<boolean>();
  isDeliveryActive = input<boolean>();
  deliveryTargetIcons = input<any>();
  contingents = input<any>();
  deliveryTargets = input<any>();
  separatorKeysCodes = input<any>();
  buttonsTemplate = input<TemplateRef<any>>();
  productList = input<any>();
  eopTypesWithDelivery = input<string[]>();
  egpTypes = input<any>();
  egwgmaSmsPause = input<any>();
  egwsummerConfig = input<any>();

  deliverySeyActive = output<MatCheckboxChange>();
  remove = output<RemoveEvent>();
  add = output<AddEvent>();
  expandedChanged = output<string>();
  deliveryChange = output<DeliveryChange>();
  smsPauseChange = output<{
    checked: boolean;
    productType: 'egwgma' | 'egwsummer';
  }>();
  changePause = output<ChangePause>();
  cancel = output();
  summarizeUser = output();

  accordionEopDelivery = viewChild<MatAccordion>('accordionEopDelivery');
  accordionEppDelivery = viewChild<MatAccordion>('accordionEppDelivery');
  accordionEgpDelivery = viewChild<MatAccordion>('accordionEgpDelivery');
  accordionEgwDelivery = viewChild<MatAccordion>('accordionEgwDelivery');
  accordionEgwGmaDelivery = viewChild<MatAccordion>('accordionEgwGmaDelivery');

  dateToFormControlErrors = signal<ValidationErrors | null>(null);
  activeFormControlValue = signal<any>(null);

  nameFormControlValue = signal<any>(null);
  emailFormControlValue = signal<any>(null);

  ngOnInit(): void {
    this.setDateToFormControlErrors();
    this.setActiveFormControlValue();
    this.setNameFormControlValue();
    this.setEmailFormControlValue();
  }

  onDeliverySetActive(event: MatCheckboxChange) {
    this.deliverySeyActive.emit(event);
  }

  onRemove(event: RemoveEvent) {
    this.remove.emit(event);
  }

  onAdd(event: AddEvent) {
    this.add.emit(event);
  }

  onExpandedChanged(section: string) {
    this.expandedChanged.emit(section);
  }

  onDeliveryChange(event: MatCheckboxChange, product, formControl: string) {
    this.deliveryChange.emit({ event, product, formControl });
  }

  onSmsPauseChange(checked: boolean, productType: 'egwgma' | 'egwsummer') {
    this.smsPauseChange.emit({ checked, productType });
  }

  onChangePause(productType: 'egwgma' | 'egwsummer', section: string, event) {
    this.changePause.emit({ productType, section, event });
  }

  onCancel() {
    this.cancel.emit();
  }

  onSummarizeUser() {
    this.summarizeUser.emit();
  }

  private setDateToFormControlErrors() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupDelivery;
        const control = fg?.get('dateTo');
        if (!control) {
          this.dateToFormControlErrors.set(null);
          return;
        }

        const sub = merge(control.valueChanges, control.statusChanges)
          .pipe(
            startWith(null),
            map(() => control.errors)
          )
          .subscribe(errs => this.dateToFormControlErrors.set(errs));

        onCleanup(() => {
          sub.unsubscribe();
          this.dateToFormControlErrors.set(null);
        });
      },
      { injector: this.injector }
    );
  }

  private setActiveFormControlValue() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupDelivery;
        const control = fg?.get('active');
        if (!control) {
          this.activeFormControlValue.set(null);
          return;
        }

        const sub = merge(control.valueChanges)
          .pipe(startWith(control.value))
          .subscribe(value => this.activeFormControlValue.set(value));

        onCleanup(() => {
          sub.unsubscribe();
          this.activeFormControlValue.set(null);
        });
      },
      { injector: this.injector }
    );
  }

  private setNameFormControlValue() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupStammdaten;
        const control = fg?.get('name');
        if (!control) {
          this.nameFormControlValue.set(null);
          return;
        }

        const sub = merge(control.valueChanges)
          .pipe(startWith(control.value))
          .subscribe(value => this.nameFormControlValue.set(value));

        onCleanup(() => {
          sub.unsubscribe();
          this.nameFormControlValue.set(null);
        });
      },
      { injector: this.injector }
    );
  }

  private setEmailFormControlValue() {
    effect(
      onCleanup => {
        const fg = this.userService.formGroupStammdaten;
        const control = fg?.get('email');
        if (!control) {
          this.emailFormControlValue.set(null);
          return;
        }

        const sub = merge(control.valueChanges)
          .pipe(startWith(control.value))
          .subscribe(value => this.emailFormControlValue.set(value));

        onCleanup(() => {
          sub.unsubscribe();
          this.emailFormControlValue.set(null);
        });
      },
      { injector: this.injector }
    );
  }
}
